import { useState, useEffect } from "react";
import { generateAuditedClient } from "../audit/auditedClient";
import { getFeatureFlags as getFeatureFlagsQuery } from "../graphql/queries";
import type { FeatureFlag } from "../models/API";

const client = generateAuditedClient();

const STORAGE_KEY = "spbfinn:featureFlags";
const TTL_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_VERSION = 1;

/**
 * Shape returned to consumers. Boolean projection of the SSM string values.
 *
 * Adding a new flag is just an SSM put + a new key here — no code change needed
 * if the consumer accesses via `flags[name]` with a fallback. The named boolean
 * keys are convenience accessors for known flags.
 */
export interface FeatureFlags {
  /** When true, the per-callout HTML fallback fires if `getCallouts` returns []. */
  UNIFIED_CALLOUT_FALLBACK: boolean;
  /**
   * Report subscriptions & weekly / daily digests (FINN 3 / F3). Off hides the
   * Subscribe control and the My subscriptions dashboard; the scheduler reads
   * the same SSM parameter, so one switch covers both (design D10).
   */
  REPORT_DIGEST_ENABLED: boolean;
  /** Catch-all for future string flags read by name. */
  [key: string]: boolean | undefined;
}

const DEFAULTS: FeatureFlags = {
  UNIFIED_CALLOUT_FALLBACK: true, // safe rollout default per spec
  // Email is hard to take back: dark until the flag is read as true.
  REPORT_DIGEST_ENABLED: false,
};

interface CachedFlags {
  v: number;
  ts: number;
  flags: FeatureFlags;
}

function parseFromCache(): FeatureFlags | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedFlags;
    if (
      !parsed ||
      parsed.v !== STORAGE_VERSION ||
      typeof parsed.ts !== "number"
    ) {
      return null;
    }
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return { ...DEFAULTS, ...parsed.flags };
  } catch {
    return null;
  }
}

function saveToCache(flags: FeatureFlags): void {
  try {
    const payload: CachedFlags = {
      v: STORAGE_VERSION,
      ts: Date.now(),
      flags,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be disabled (incognito, quota); silently ignore
  }
}

function rowsToFlags(rows: FeatureFlag[]): FeatureFlags {
  const out: FeatureFlags = { ...DEFAULTS };
  rows.forEach((r) => {
    out[r.name] = String(r.value).toLowerCase() === "true";
  });
  return out;
}

/**
 * Fetches feature flags from AppSync (`getFeatureFlags` Lambda passthrough)
 * with a localStorage cache (5-minute TTL).
 *
 * On query failure we fall back to {@link DEFAULTS} (`UNIFIED_CALLOUT_FALLBACK = true`)
 * — that's the safest value during the rollout window per the design.
 */
export function useFeatureFlags(): {
  flags: FeatureFlags;
  loading: boolean;
} {
  const initial = parseFromCache();
  const [flags, setFlags] = useState<FeatureFlags>(initial ?? DEFAULTS);
  const [loading, setLoading] = useState<boolean>(initial === null);

  useEffect(() => {
    if (initial !== null) {
      // Cache hit; no fetch needed.
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await client.graphql({ query: getFeatureFlagsQuery });
        if (cancelled) return;
        const rows: FeatureFlag[] = (result as any).data?.getFeatureFlags ?? [];
        const next = rowsToFlags(rows);
        setFlags(next);
        saveToCache(next);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          "[useFeatureFlags] failed to fetch flags — using defaults",
          err
        );
        // Defaults are already in state; nothing else to do.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { flags, loading };
}
